import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useFormApi,
  useFormState,
  Table,
  Button,
  ButtonVariants,
  Alert,
  Typography,
  TypographyVariants,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader } from '@/hooks';
import { EMPTY_LIST, cx } from '@/lib/utils';
import { buildTableLabels } from '@/config/tableLabels';
import { SummaryTileRow } from '../SummaryTiles';
import { PickFromTableModal } from './PickFromTableModal';
import styles from './SelectFromTable.module.scss';

const getRowId = (row) => row.id;

const DEFAULT_PAGE_SIZE = 20;

/**
 * Props:
 *   config   what to pick and how to show it — the whole section, including its own query
 *   context  whatever the config's builders need from the screen (the record, values, …)
 *   disabled locks the button while the screen cannot pick yet
 *   note     shown under the button — why it is locked
 *   promptClassName  sizes the button block, for a screen that lines it up with its own grid
 */
// Pick rows from a modal table, then show them in one. Self-contained: a config is enough.
export const SelectFromTable = ({ config, context, disabled = false, note, promptClassName }) => {
  const t = useTranslations();
  const { setValue } = useFormApi();
  const { values } = useFormState();
  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState(EMPTY_LIST);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The config brings its own read; nothing is fetched until the modal is open.
  const {
    rows: candidates = EMPTY_LIST,
    total = 0,
    loading = false,
  } = config.useCandidates({
    context,
    search,
    page,
    pageSize,
    enabled: isOpen,
  });

  const handleOpen = useCallback(() => {
    setPage(1);
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleSelect = useCallback(
    (picked) => {
      setSelected((previous) => {
        if (config.selectMode !== 'multi') return picked;
        const existing = new Set(previous.map((row) => row.id));
        return [...previous, ...picked.filter((row) => !existing.has(row.id))];
      });
      setOpen(false);
    },
    [config.selectMode],
  );

  const handleRemove = useCallback((rowId) => {
    setSelected((previous) => previous.filter((row) => row.id !== rowId));
  }, []);

  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(
    selected,
    config.unitOptions,
    t,
  );

  const builderArgs = useMemo(
    () => ({
      unitHeaderSelect,
      selections,
      selected,
      values,
      onRemove: handleRemove,
      context,
    }),
    [unitHeaderSelect, selections, selected, values, handleRemove, context],
  );

  // Only the picks drive this: `context` is a fresh object per render, so it would loop.
  const writeArgs = useRef();
  writeArgs.current = { write: config.writeSelection, context, setValue };
  useEffect(() => {
    const { write, context: ctx, setValue: set } = writeArgs.current;
    write?.({ selected, selections, setValue: set, context: ctx });
  }, [selected, selections]);

  const columns = useMemo(
    () => config.buildSelectedColumns?.(t, builderArgs) ?? EMPTY_LIST,
    [config, t, builderArgs],
  );
  const tiles = useMemo(
    () => config.buildTiles?.(t, builderArgs) ?? EMPTY_LIST,
    [config, t, builderArgs],
  );
  const alerts = useMemo(
    () => config.buildAlerts?.(t, builderArgs) ?? EMPTY_LIST,
    [config, t, builderArgs],
  );
  const footerRow = useMemo(
    () => config.buildFooterRow?.(t, builderArgs),
    [config, t, builderArgs],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const hasSelection = selected.length > 0;
  // A multi-select section keeps its button, so more rows can be added to what is there.
  const showPrompt = !hasSelection || config.keepPrompt;

  return (
    <>
      {showPrompt ? (
        <div className={cx(styles.prompt, promptClassName)}>
          {config.descriptionKey ? (
            <Typography variant={TypographyVariants.bodySm} color={TypographyColors.secondary}>
              {t(config.descriptionKey)}
            </Typography>
          ) : null}
          <Button
            variant={config.buttonVariant ?? ButtonVariants.primary}
            fullWidth
            onClick={handleOpen}
            disabled={disabled}
          >
            {t(config.buttonKey ?? config.titleKey)}
          </Button>
          {note ? (
            <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.error}>
              {note}
            </Typography>
          ) : null}
        </div>
      ) : null}

      {alerts.map((alert) => (
        <Alert key={alert.id} variant={alert.variant}>
          {alert.message}
        </Alert>
      ))}

      {hasSelection ? (
        <div className={styles.selection}>
          {tiles.length > 0 ? <SummaryTileRow tiles={tiles} /> : null}
          <UnitTypeContext.Provider value={contextValue}>
            <Table
              columns={columns}
              data={selected}
              getRowId={getRowId}
              showPagination={false}
              footerRow={footerRow}
              labels={tableLabels}
            />
          </UnitTypeContext.Provider>
        </div>
      ) : null}

      {/* Closing unmounts it, so nothing it held survives into the next open. */}
      {isOpen ? (
        <PickFromTableModal
          open
          onClose={handleClose}
          config={config}
          rows={candidates}
          total={total}
          loading={loading}
          onSearchChange={setSearch}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onConfirm={handleSelect}
        />
      ) : null}
    </>
  );
};
